import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';

// Contextos
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';

// API
import { useParticipanteService } from '../../services/participanteServices';

// Estilos
import styles from './loginparticipantepage.module.css';

const DEBUG = false;
const dbg = (...args) => { if (DEBUG) console.log('[LoginParticipantePage]', ...args); };

/**
 * Extrai "from" (rota original protegida) + identifica se veio de /res/:idTurma ou /ead/:idTurma
 * - dest: 'res' | 'ead' | ''
 * - turmaHash: string | null
 */
function parseFrom(location) {
  const from = location?.state?.from || null;
  const pathname = from?.pathname || '';
  const search = from?.search || '';
  const hash = from?.hash || '';

  // aceita /res/:idTurma, /responder/:idTurma e /ead/:idTurma
  const match = pathname.match(/^\/(res(?:ponder)?|ead)\/([^/?#]+)/i);
  const rawDest = match?.[1] || null;
  const turmaHash = match?.[2] || null;

  const dest = rawDest
    ? (rawDest.toLowerCase().startsWith('ead') ? 'ead' : 'res')
    : '';

  return { from, pathname, search, hash, turmaHash, dest };
}

function LoginParticipantePage() {
  const { isAuthenticated } = useAuth();
  const { obterParticipantePorCelular } = useParticipanteService();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const [telefone, setTelefone] = useState('');
  const [carregando, setCarregando] = useState(false);

  const telefoneInputRef = useRef(null);

  const fromInfo = useMemo(() => parseFrom(location), [location]);
  const turmaHash = fromInfo.turmaHash || '';
  const dest = fromInfo.dest || '';

  // 📱 Máscara telefone
  const aplicarMascaraTelefone = (valor) => {
    const numeros = valor.replace(/\D/g, '').slice(0, 11);
    const parte1 = numeros.slice(0, 2);
    const parte2 = numeros.slice(2, 7);
    const parte3 = numeros.slice(7, 11);

    if (numeros.length <= 2) return parte1 ? `(${parte1}` : '';
    if (numeros.length <= 7) return `(${parte1}) ${parte2}`;
    return `(${parte1}) ${parte2}-${parte3}`;
  };

  const handleTelefoneChange = (e) => {
    const valor = e.target.value;
    setTelefone(aplicarMascaraTelefone(valor));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCarregando(true);

    const telefoneLimpo = telefone.replace(/\D/g, '');

    if (telefoneLimpo.length !== 11) {
      showToast('O telefone deve conter 11 dígitos.', 3000, 'error');
      setCarregando(false);
      return;
    }

    dbg('fromInfo:', fromInfo);

    try {
      const res = await obterParticipantePorCelular(telefoneLimpo);

      if (res?.success && res?.data) {
        // Participante existe -> ir para página de senha com cel + turma + dest na URL
        const search = new URLSearchParams({
          cel: telefoneLimpo,
          turma: turmaHash || '',
          dest: dest || '',
        }).toString();

        navigate(`/participante/senha?${search}`, {
          replace: true,
          state: { from: location.state?.from || null }, // preserva rota original
        });
      } else {
        showToast('Participante não encontrado para este celular.', 3000, 'error');
      }
    } catch (err) {
      dbg('obterParticipantePorCelular erro:', err);
      showToast('Falha ao verificar o celular. Tente novamente.', 3000, 'error');
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    dbg('mount -> location:', location);
    dbg('mount -> parseFrom:', fromInfo);
  }, [location, fromInfo]);

  // Se já autenticado, volta pra rota original (state.from) ou fallback com turma+dest ou /participante/page
  useEffect(() => {
    if (isAuthenticated) {
      const fallback =
        (turmaHash && (dest === 'res' || dest === 'ead')) ? `/${dest}/${turmaHash}` : '/participante/page';

      const caminho = location.state?.from?.pathname || fallback;

      dbg('isAuthenticated -> redirecionando para:', caminho);
      navigate(caminho, { replace: true });
    }
  }, [isAuthenticated, location.state, navigate, turmaHash, dest]);

  useEffect(() => {
    telefoneInputRef.current?.focus();
  }, []);

  // URL do cadastro preservando turma/dest
  const cadastroSearch = useMemo(() => {
    const params = new URLSearchParams();
    if (turmaHash) params.set('turma', turmaHash);
    if (dest) params.set('dest', dest);
    const s = params.toString();
    return s ? `?${s}` : '';
  }, [turmaHash, dest]);

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>Login</h2>

      {DEBUG && (
        <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
          <div><strong>from.pathname:</strong> {fromInfo.pathname || '(vazio)'} </div>
          <div><strong>turmaHash:</strong> {turmaHash || '(não encontrado)'}</div>
          <div><strong>dest:</strong> {dest || '(não encontrado)'}</div>
        </div>
      )}

      <form onSubmit={handleSubmit} className={styles.form}>
        <div>
          <p>Digite seu número de telefone para localizar seu cadastro.</p>
        </div>

        <input
          type="text"
          ref={telefoneInputRef}
          placeholder="(11) 91234-5678"
          value={telefone}
          onChange={handleTelefoneChange}
          className={styles.input}
          required
        />

        <button type="submit" className={styles.button} disabled={carregando}>
          {carregando ? 'Verificando...' : 'Continuar'}
        </button>

        <p className={styles.linkText}>
          Ainda não possui acesso?{' '}
          <Link
            to={`/participante/cadastro${cadastroSearch}`}
            state={{ from: location.state?.from || null }}   // ✅ React Router v6
            className={styles.link}
          >
            CADASTRE-SE
          </Link>
        </p>
      </form>
    </div>
  );
}

export default LoginParticipantePage;